import { api } from "./index";

export interface SingleSourceForm {
  id: string;
  formNumber: string;
  title: string;
  date: string;
  projectName: string;
  projectLocation: string;
  totalBudget?: number;
  amountAllocatedFromCurrentYearBudget?: number;
  totalExpenditureTillDate?: number;
  remainingBudget?: number;
  vendorName: string;
  proposedCost: number;
  proposedDuration: number;
  justification: string;
  detailsOfConditions?: string;
  decisionToSingleSource?: string;
  submittedBy: string;
  approvedBy?: string;
  status: 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'AWARDED';
  createdAt: string;
  updatedAt: string;
  doc: {
    id: string;
    fileName: string;
    bucket: string;
    objectKey: string;
  };
  meeting?: {
    id: string;
    title: string;
    scheduledDate: string;
    attendees: Array<{
      member: {
        id: string;
        fullName: string;
        fullNameDhivehi?: string | null;
      };
    }>;
  };
  approvals?: Array<{
    id: string;
    stage: 'HEAD_OF_PROCUREMENT';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    comments?: string;
    approvedAt?: string;
    rejectedAt?: string;
    approver: {
      id: string;
      fullName: string;
      fullNameDhivehi?: string | null;
      email: string;
      role: string;
    };
  }>;
}

export type SingleSourceFormWithPending = SingleSourceForm & {
  submitter?: {
    id: string;
    fullName: string;
    fullNameDhivehi?: string | null;
    email: string;
    role: string;
  } | null;
  approval: {
    id: string;
    stage: 'HEAD_OF_PROCUREMENT';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    approver: {
      id: string;
      fullName: string;
      fullNameDhivehi?: string | null;
      email: string;
      role: string;
    };
  };
};

export interface CreateSingleSourceFormDto {
  formNumber?: string;
  date: string;
  projectName: string;
  projectLocation: string;
  totalBudget?: number;
  amountAllocatedFromCurrentYearBudget?: number;
  totalExpenditureTillDate?: number;
  remainingBudget?: number;
  vendorName: string;
  proposedCost: number;
  proposedDuration: number;
  justification: string;
  detailsOfConditions?: string;
  decisionToSingleSource?: string;
  fileName?: string;
  base64File?: string;
}

export interface UpdateSingleSourceFormDto {
  formNumber?: string;
  date?: string;
  projectName?: string;
  projectLocation?: string;
  totalBudget?: number;
  amountAllocatedFromCurrentYearBudget?: number;
  totalExpenditureTillDate?: number;
  remainingBudget?: number;
  vendorName?: string;
  proposedCost?: number;
  proposedDuration?: number;
  justification?: string;
  detailsOfConditions?: string;
  decisionToSingleSource?: string;
  status?: 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'AWARDED';
  meetingId?: string;
}

export interface UploadSingleSourceDocumentDto {
  fileName: string;
  base64File: string;
}

export interface SingleSourceFormListResponse {
  forms: SingleSourceForm[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const singleSourceApi = {
  getNextFormNumber: async (year?: number): Promise<{ formNumber: string }> => {
    const response = await api.get<{ formNumber: string }>('/single-source/next-number', {
      params: year != null ? { year } : undefined,
    });
    return response.data;
  },

  // Create single source form
  createForm: (data: CreateSingleSourceFormDto) =>
    api.post<SingleSourceForm>('/single-source', data),

  // List single source forms
  listForms: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<SingleSourceFormListResponse>('/single-source', { params }),

  // Get single source form by ID
  getFormById: (id: string) =>
    api.get<SingleSourceForm>(`/single-source/${id}`),

  // Update single source form
  updateForm: (id: string, data: UpdateSingleSourceFormDto) =>
    api.put<SingleSourceForm>(`/single-source/${id}`, data),

  // Delete single source form
  deleteForm: (id: string) =>
    api.delete<{ message: string }>(`/single-source/${id}`),

  // Upload single source form document
  uploadDocument: (id: string, data: UploadSingleSourceDocumentDto) =>
    api.post<SingleSourceForm>(`/single-source/${id}/document`, data),

  // Download single source form document
  downloadDocument: (id: string) =>
    api.get(`/single-source/${id}/download`, { responseType: 'blob' }),

  // Submit single source form for review
  submitForm: (id: string) =>
    api.post<SingleSourceForm>(`/single-source/${id}/submit`),

  // Generate PDF for single source form
  generatePDF: (id: string) =>
    api.get(`/single-source/${id}/pdf`, { responseType: 'blob' }),

  // Approve single source form
  approveForm: (id: string, data?: { comments?: string }) =>
    api.post<SingleSourceForm>(`/single-source/${id}/approve`, data),

  // Reject single source form
  rejectForm: (id: string, data: { comments: string }) =>
    api.post<SingleSourceForm>(`/single-source/${id}/reject`, data),

  // Get pending approvals
  getPendingApprovals: () =>
    api.get<SingleSourceFormWithPending[]>('/single-source/approvals/pending'),
};
